import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { goChecklistService } from '../services/go-checklist.service';
import type { OverrideChecklistItemInput } from '../types/go-checklist.types';

const KEYS = {
  current: (opportunityId: number) =>
    ['go-checklist', 'current', opportunityId] as const,
  history: (opportunityId: number) =>
    ['go-checklist', 'history', opportunityId] as const,
};

export function useGoChecklist(opportunityId: number) {
  return useQuery({
    queryKey: KEYS.current(opportunityId),
    queryFn: () => goChecklistService.getCurrent(opportunityId),
    refetchInterval: 10_000, // toast l'auto-recompute si arrive dans la session
  });
}

export function useGoChecklistHistory(opportunityId: number, enabled = true) {
  return useQuery({
    queryKey: KEYS.history(opportunityId),
    queryFn: () => goChecklistService.getHistory(opportunityId),
    enabled,
  });
}

export function useRecomputeChecklist(opportunityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => goChecklistService.recompute(opportunityId),
    onSuccess: (result) => {
      toast.success(
        `Checklist recalculée — verdict : ${formatVerdict(result.verdict)}`,
      );
      qc.invalidateQueries({ queryKey: ['go-checklist'] });
    },
    onError: (err: Error) => {
      toast.error(`Recalcul échoué : ${err.message}`);
    },
  });
}

export function useOverrideChecklistItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      input,
    }: {
      itemId: number;
      input: OverrideChecklistItemInput;
    }) => goChecklistService.overrideItem(itemId, input),
    onSuccess: () => {
      toast.success('Override appliqué');
      qc.invalidateQueries({ queryKey: ['go-checklist'] });
    },
    onError: (err: Error) => {
      toast.error(`Override refusé : ${err.message}`);
    },
  });
}

export function useClearOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: number) => goChecklistService.clearOverride(itemId),
    onSuccess: () => {
      toast.success('Override réinitialisé');
      qc.invalidateQueries({ queryKey: ['go-checklist'] });
    },
  });
}

function formatVerdict(v: string): string {
  const labels: Record<string, string> = {
    GO_RECOMMENDED: 'GO',
    GO_WITH_RESERVES: 'GO avec réserves',
    NO_GO_RECOMMENDED: 'NO-GO',
    UNDETERMINABLE: 'Indéterminable',
  };
  return labels[v] ?? v;
}
